import { useParams } from "react-router-dom";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { fetchProjectService } from "../../../features/project/service/project-service";
import { reportFinancialService, reportFinancialBothService } from "../../../features/financial/service/financeiro-service";
import { fetchRubricasGrouped } from "../../../features/rubrica/service/rubrica";
import { fetchTiposLancamento } from "../../../features/lancamento/service/tipo-lancamento";
import { fetchBeneficiaryService } from "../../../features/beneficiary/service/beneficiary-service";
import MenuTop from "../../../components/menu/top/menu-top";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FinancialSummary from "./financial-sumary";
import TimelineIcon from '@mui/icons-material/Timeline';
import CustomStepper from "../../../components/stepper/stepper";
import SimpleTable from "../../../components/table/table";
import StepContent from "./step-content";
import Icons from "../../../components/icons/iconts";
import ExcelService from "../../../features/reports/services/excel-service";
import PdfService from "../../../features/reports/services/pdf-service";
import Loader from "../../../components/loader/loader";
import { Movimentacao } from "../interfaces/report-data";
import './style.css';

const steps = ['Tipo de Lançamento', 'Rubrica', 'Período'];

export interface FinancialReportParams { 
    lancamentoType: string;
    rubricaId?: string;
    startDate: string;
    endDate: string;
}

const FinancialReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [ isLoading, setIsLoading ] = useState(true);
    const [ isGeneratingReport, setIsGeneratingReport ] = useState(false);
    const [ projectData, setProjectData ] = useState<any>(null);
    const [ reportData, setReportData ] = useState<any>(null);
    const [ reportType, setReportType ] = useState('');
    const [ reportName, setReportName ] = useState('');
    const [ reportGenerated, setReportGenerated ] = useState(false);
    const [ useFullPeriod, setUseFullPeriod ] = useState(false);
    
    // Estado para armazenar os tipos de lançamento da API
    const [ lancamentoTypes, setLancamentoTypes ] = useState<Array<{
        id: string;
        name: string;
        description: string;
    }>>([]);
    
    // Estado para armazenar as rubricas agrupadas
    const [ rubricas, setRubricas ] = useState<Array<any>>([]);
    
    const [reportParams, setReportParams] = useState({
        lancamentoType: '',
        rubricaId: '',
        startDate: '',
        endDate: '',
    });

    const tableColumns = [
        { id: 'descricao', label: 'Descrição' },
        { id: 'beneficiaryName', label: 'Beneficiário' },
        { id: 'rubrica', label: 'Rubrica'},
        { id: 'tipo', label: 'Tipo' },
        { 
            id: 'valor', 
            label: 'Valor (R$)', 
            align: 'right',
            render: (value: number) => `R$ ${(value/100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        }
    ];

    const resolveBeneficiaryId = useCallback((movimentacao: any) => {
        if (!movimentacao) return '';
        if (typeof movimentacao.idBeneficiary === 'string' && movimentacao.idBeneficiary) return movimentacao.idBeneficiary;
        if (typeof movimentacao.beneficiaryId === 'string' && movimentacao.beneficiaryId) return movimentacao.beneficiaryId;
        if (movimentacao.beneficiary && typeof movimentacao.beneficiary === 'object' && movimentacao.beneficiary.id) {
            return movimentacao.beneficiary.id;
        }
        return '';
    }, []);

    const resolveBeneficiaryName = useCallback((movimentacao: any) => {
        if (!movimentacao) return '-';
        if (typeof movimentacao.beneficiaryName === 'string' && movimentacao.beneficiaryName.trim()) return movimentacao.beneficiaryName.trim();
        if (typeof movimentacao.nomeBeneficiario === 'string' && movimentacao.nomeBeneficiario.trim()) return movimentacao.nomeBeneficiario.trim();
        if (movimentacao.beneficiary && typeof movimentacao.beneficiary === 'object' && movimentacao.beneficiary.name) {
            return movimentacao.beneficiary.name;
        }
        if (typeof movimentacao.beneficiary === 'string' && movimentacao.beneficiary.trim()) {
            const beneficiaryId = resolveBeneficiaryId(movimentacao);
            if (!beneficiaryId || movimentacao.beneficiary !== beneficiaryId) {
                return movimentacao.beneficiary.trim();
            }
        }
        return '-';
    }, [resolveBeneficiaryId]);

    const enrichMovimentacoesWithBeneficiaries = useCallback(async (movimentacoes?: Movimentacao[]) => {
        if (!Array.isArray(movimentacoes) || movimentacoes.length === 0) {
            return movimentacoes || [];
        }

        const idsToResolve = Array.from(
            new Set(
                movimentacoes
                    .map((movimentacao) => {
                        const beneficiaryId = resolveBeneficiaryId(movimentacao);
                        const beneficiaryName = resolveBeneficiaryName(movimentacao);
                        return beneficiaryId && beneficiaryName === '-' ? beneficiaryId : null;
                    })
                    .filter(Boolean) as string[]
            )
        );

        const resolvedEntries = await Promise.all(
            idsToResolve.map(async (beneficiaryId) => {
                try {
                    const beneficiary = await fetchBeneficiaryService(beneficiaryId);
                    return [beneficiaryId, beneficiary?.name || '-'] as const;
                } catch {
                    return [beneficiaryId, '-'] as const;
                }
            })
        );

        const beneficiaryNameMap = new Map<string, string>(resolvedEntries);

        return movimentacoes.map((movimentacao) => {
            const beneficiaryId = resolveBeneficiaryId(movimentacao);
            const fallbackName = resolveBeneficiaryName(movimentacao);

            return {
                ...movimentacao,
                beneficiaryName: beneficiaryId
                    ? (beneficiaryNameMap.get(beneficiaryId) || fallbackName)
                    : fallbackName,
            };
        });
    }, [resolveBeneficiaryId, resolveBeneficiaryName]);

    const enrichReportBeneficiaries = useCallback(async (report: any) => {
        if (!report) return report;

        const [movimentacoes, receitas, despesas] = await Promise.all([
            enrichMovimentacoesWithBeneficiaries(report.movimentacoes),
            enrichMovimentacoesWithBeneficiaries(report.receitas),
            enrichMovimentacoesWithBeneficiaries(report.despesas),
        ]);

        return {
            ...report,
            movimentacoes,
            receitas,
            despesas,
        };
    }, [enrichMovimentacoesWithBeneficiaries]);

    // Função para buscar tipos de lançamento da API
    const fetchLancamentoTypes = useCallback(async () => {
        try {
            const response = await fetchTiposLancamento();
            
            // Transformar os dados da API para o formato esperado pelo componente
            const formattedTypes = response.map((lancamento: any) => ({
                id: lancamento.id,
                name: lancamento.name,
                description: lancamento.description
            }));

            // Adicionar a opção "Ambos" manualmente
            const typesWithBoth = [
                ...formattedTypes,
                { id: 'both', name: 'Ambos', description: 'Receita/Despesa do Projeto' }
            ];
            
            setLancamentoTypes(typesWithBoth);
        } catch (error) {
            console.error("Erro ao buscar tipos de lançamento:", error);
            showAlert('Erro ao carregar tipos de lançamento', SEVERITY.ERROR);
            
            // Fallback para valores mockados em caso de erro
            const fallbackTypes = [
                { id: '1e7f6c32-6cd4-4bac-847c-925b60bb63de', name: 'Despesa', description: 'Despesa do Projeto' },
                { id: 'd52c707f-3432-4864-b9e7-6924a2ebfedd', name: 'Receita', description: 'Receita do Projeto' },
                { id: 'both', name: 'Ambos', description: 'Receita/Despesa do Projeto' }
            ];
            setLancamentoTypes(fallbackTypes);
        }
    }, [showAlert]);

    // Função para buscar rubricas agrupadas da API
    const fetchRubricas = useCallback(async () => {
        try {
            const response = await fetchRubricasGrouped();
            
            // Usar os dados diretamente da API como no FinancialEdit
            setRubricas(response || []);
        } catch (error) {
            console.error("Erro ao buscar rubricas agrupadas:", error);
            showAlert('Erro ao carregar rubricas', SEVERITY.ERROR);
            
            // Fallback para array vazio em caso de erro
            setRubricas([]);
        }
    }, [showAlert]);

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return !!reportParams.lancamentoType;
            case 1:
                return reportParams.lancamentoType === 'both' || !!reportParams.rubricaId;
            case 2:
                return !!reportParams.startDate && !!reportParams.endDate;
            default:
                return false;
        }
    };

    const handleReset = () => {
        setReportParams({
            lancamentoType: '',
            rubricaId: '',
            startDate: '',
            endDate: '',
        });
        setReportGenerated(false);
        setUseFullPeriod(false);
    };

    const handleGenerateReport = useCallback(async () => {
        setIsGeneratingReport(true);
        
        const paramsReport: FinancialReportParams = {
            lancamentoType: reportParams.lancamentoType,
            startDate: reportParams.startDate,
            endDate: reportParams.endDate
        };

        if (reportParams.lancamentoType !== 'both' && reportParams.rubricaId) {
            paramsReport.rubricaId = reportParams.rubricaId;
        }

        try {
            let responseReport;
            
            if(reportParams.lancamentoType === 'both') {
                responseReport = await reportFinancialBothService(id as string, paramsReport);
            } else {
                responseReport = await reportFinancialService(id as string, paramsReport);
            }

            responseReport = await enrichReportBeneficiaries(responseReport);
            
            if (!responseReport.name && projectData?.name) {
                responseReport.name = projectData.name;
            }
            
            setReportData(responseReport);
            setReportGenerated(true);
            showAlert('Relatório gerado com sucesso!', SEVERITY.SUCCESS);
        } catch (error) {
            console.error('Erro ao gerar relatório:', error);
            setReportGenerated(false);
            showAlert('Erro ao gerar relatório', SEVERITY.ERROR);
        } finally {
            setIsGeneratingReport(false);
        }
    }, [enrichReportBeneficiaries, id, projectData?.name, reportParams, showAlert]);

    const fetchRubricaName = useCallback((id: string) => {
        const rubrica = rubricas.find(r => r.id === id);
        if (rubrica) {
            setReportName(rubrica.name);
        }
    }, [rubricas]);

    const handleInputChange = async (e: ChangeEvent<HTMLInputElement>)  => {
        const { name, value } = e.target;
        const lancamento = lancamentoTypes.find(lancamento => lancamento.id === value);
        
        if(lancamento) {
            setReportType(lancamento.name);
        } else {
            setReportType('Geral');
        }
        
        setReportParams({
            ...reportParams,
            [name]: value,
        });
    };

    const handleAutocompleteChange = (option: any) => {
        const rubrica = rubricas.find(rubrica => rubrica.id === option);

        if (rubrica) {
            setReportName(rubrica.name);
        } else {
            setReportName('Geral');
        }

        setReportParams(prev => ({
            ...prev,
            rubricaId: option || ''
        }));
    };

    const handleDateChange = (field: string, value: string) => {
        setReportParams(prevParams => ({
            ...prevParams,
            [field]: value || ''
        }));
    };

    const handleFullPeriodChange = (checked: boolean) => {
        setUseFullPeriod(checked);
        if (checked && projectData) {
            setReportParams(prev => ({
                ...prev,
                startDate: projectData.initialDate || '',
                endDate: new Date().toISOString().split('T')[0]
            }));
        }
    };

    const getSelectedRubrica = () => {
        if (!reportParams.rubricaId) return null;
        return rubricas.find(r => r.id === reportParams.rubricaId) || null;
    };

    const selectedRubrica = getSelectedRubrica();

    const fetchAllData = useCallback(async (projectId: string) => {
        setIsLoading(true);
        try {
            // Buscar dados do projeto, tipos de lançamento e rubricas em paralelo
            const [projectResponse] = await Promise.all([
                fetchProjectService(projectId),
                fetchLancamentoTypes(),
                fetchRubricas()
            ]);

            setProjectData(projectResponse);
            return projectResponse;
        } catch (error) {
            console.error("Error fetching project data:", error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [fetchLancamentoTypes, fetchRubricas]);

    const handleDownload = async () => {
        try {
            setIsGeneratingReport(true);
            
            const projectName = reportData?.name || projectData?.name || 'Relatório';            
            await ExcelService.exportToExcel(reportData, reportType, projectName);
            
            showAlert('Relatório Excel gerado com sucesso!', SEVERITY.SUCCESS);
        } catch (error) {
            console.error('Erro ao exportar para Excel:', error);
            showAlert('Erro ao exportar relatório Excel', SEVERITY.ERROR);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            setIsGeneratingReport(true);
            
            const projectName = reportData?.name || projectData?.name || 'Relatório';            
            await PdfService.exportToPDF(reportData, reportType, projectName);
            
            showAlert('Relatório PDF gerado com sucesso!', SEVERITY.SUCCESS);
        } catch (error) {
            console.error('Erro ao exportar para PDF:', error);
            showAlert('Erro ao exportar relatório PDF', SEVERITY.ERROR);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    useEffect(() => {
        fetchAllData(id as string);
    }, [id, fetchAllData]);

    const getActiveSteps = () => {
        if (reportParams.lancamentoType === 'both') {
            return ['Tipo de Lançamento', 'Período'];
        }
        return steps;
    };

    const renderStepContent = (step: number) => {
        if (reportParams.lancamentoType === 'both' && step >= 1) {
            return (
                <StepContent
                    step={2}
                    reportParams={reportParams}
                    lancamentoTypes={lancamentoTypes}
                    rubricas={rubricas}
                    onInputChange={handleInputChange}
                    onAutocompleteChange={handleAutocompleteChange}
                    onDateChange={handleDateChange}
                    selectedRubrica={selectedRubrica}
                    useFullPeriod={useFullPeriod}
                    onFullPeriodChange={handleFullPeriodChange}
                />
            );
        }
        
        return (
            <StepContent
                step={step}
                reportParams={reportParams}
                lancamentoTypes={lancamentoTypes}
                rubricas={rubricas}
                onInputChange={handleInputChange}
                onAutocompleteChange={handleAutocompleteChange}
                onDateChange={handleDateChange}
                selectedRubrica={selectedRubrica}
                useFullPeriod={useFullPeriod}
                onFullPeriodChange={handleFullPeriodChange}
            />
        );
    };

    const buttonLabels = {
        back: "Voltar",
        cancel: "Cancelar",
        next: "Concluir Etapa",
        complete: "Finalizar",
        reset: "Reiniciar",
        finish: "Gerar Relatório"
    };

    return (
        <>
            {isGeneratingReport && <Loader />}
            
            <div className="container-financial-report">
                <MenuTop />
                <div className="container-financial-report__container">
                    {!isLoading && (
                        <>
                            <Typography variant="h5" component="h1" sx={{ mb: 3, color: '#4aaa9c', display: 'flex', alignItems: 'center' }}>
                                <TimelineIcon sx={{ mr: 1 }} />
                                Relatório Financeiro {id && projectData && `- # ${projectData.name}`}
                            </Typography>
                            
                            <CustomStepper 
                                steps={getActiveSteps()}
                                renderStepContent={renderStepContent}
                                isStepValid={isStepValid}
                                onComplete={handleGenerateReport}
                                onCancel={() => navigate('/dashboard', { replace: true })}
                                onReset={handleReset}
                                buttonLabels={buttonLabels}
                            />
                            
                            {reportGenerated && reportData && (
                                <Box sx={{ mt: 4, height: '100%' }}>
                                    <Paper elevation={3} sx={{ p: 3, borderRadius: '8px', height: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#4aaa9c', fontWeight: 500, mb: 3 }}>
                                                Resultados do Relatório de {reportType} de {reportName}
                                            </Typography>

                                            <Box sx={{ display: 'flex'}}>
                                                <Icons 
                                                    iconName="InventoryOutlined" 
                                                    iconClicked={handleDownload} 
                                                    fontSize={25} 
                                                    addTooltip="Baixar relatório em excel" 
                                                    color="#4aaa9c"
                                                />
                                                <Box sx={{mr: 2 }}></Box>
                                                <Icons 
                                                    iconName="PictureAsPdfOutlined" 
                                                    iconClicked={handleDownloadPdf} 
                                                    fontSize={25} 
                                                    addTooltip="Baixar relatório em pdf" 
                                                    color="#4aaa9c"
                                                />
                                            </Box>
                                        </Box>
                                        
                                        <SimpleTable 
                                            columns={tableColumns}
                                            data={reportData?.movimentacoes || []}
                                        />

                                        <FinancialSummary 
                                            data={reportData} 
                                            reportType={reportType}
                                        />
                                    </Paper>
                                </Box>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default FinancialReport;
